import { Skeleton } from '@shared/components/shadcn';
import { twMerge } from '@shared/themes';
import { Suspense, type ComponentProps } from 'react';
import type { Option } from '../../../form-controls/types';
import { PromotionsContentSkeleton } from '../PromotionsContent/PromotionsContent';
import { PromotionsHeader, type PromotionsHeaderVariant } from '../PromotionsHeader/PromotionsHeader';
import { PromotionsPageClient, type PromotionsPageType } from './PromotionsPageClient';

type PromotionsPageComponentProps = ComponentProps<'div'> & {
  options: Option<PromotionsPageType>[];
  headerVariant?: PromotionsHeaderVariant;
  showFilters?: boolean;
  showTabSwitcher?: boolean;
  loginRequiredSections?: PromotionsPageType[];
};

type PromotionsPageFallbackProps = {
  headerVariant: PromotionsHeaderVariant;
  options: Option<PromotionsPageType>[];
  showTabSwitcher: boolean;
};

function PromotionsTabSwitcherSkeleton({ optionCount }: { optionCount: number }) {
  return (
    <div className="flex w-fit gap-3 rounded-full bg-container p-1" data-testid="promotions-tab-switcher-skeleton" aria-hidden>
      {Array.from({ length: optionCount }, (_, index) => (
        <Skeleton key={index} className="h-10 w-24 rounded-full" />
      ))}
    </div>
  );
}

function PromotionsPageFallback({ headerVariant, options, showTabSwitcher }: PromotionsPageFallbackProps) {
  return (
    <>
      {showTabSwitcher ? (
        <PromotionsHeader variant={headerVariant}>
          <PromotionsTabSwitcherSkeleton optionCount={options.length} />
        </PromotionsHeader>
      ) : null}
      <PromotionsContentSkeleton className="ll-container-inner py-4 sm:py-6" />
    </>
  );
}

function PromotionsPageComponent({
  className,
  headerVariant = 'toolbar',
  showFilters = false,
  options,
  children,
  showTabSwitcher = true,
  loginRequiredSections,
  ...props
}: PromotionsPageComponentProps) {
  return (
    <div className={twMerge('ll-grid-container', className)} {...props}>
      <Suspense fallback={<PromotionsPageFallback headerVariant={headerVariant} options={options} showTabSwitcher={showTabSwitcher} />}>
        <PromotionsPageClient
          headerVariant={headerVariant}
          showFilters={showFilters}
          options={options}
          showTabSwitcher={showTabSwitcher}
          loginRequiredSections={loginRequiredSections}
        />
      </Suspense>
      {children}
    </div>
  );
}

export { FiltersPanel, type PromotionsPageType } from './PromotionsPageClient';
export { PromotionsPageComponent, PromotionsPageFallback };
